import root from 'window-or-global';
import React, {useState, useEffect} from 'react';
import EuiTooltip, {TooltipProps} from './Tooltip';
import {withStyles} from '@material-ui/core';
import {useTrackEvent, UseTrackEventProps} from '../../hooks';

const Tooltip = withStyles(theme => ({
    tooltip: {
        color: theme.palette.common.white
    },
    popper: {
        cursor: 'pointer',
        [theme.breakpoints.up('sm')]: {
            top: '-5px !important'
        }
    }
}))(EuiTooltip);

export interface CopyProps {
    className?: string;
    text: string;
    tip?: string;
    successTip?: string;
    disabledTip?: string;
    errorTip?: string;
    onCopy?: (copied: boolean) => void;
    disabled?: boolean;
    children: TooltipProps['children'];
    trackEventProps?: UseTrackEventProps;
}

const Copy: React.FC<CopyProps> = ({
    className,
    children,
    text: copyText,
    tip = 'Copy',
    successTip = 'Copied',
    errorTip = 'Copy Failed',
    onCopy = () => {},
    disabled = false,
    trackEventProps
}) => {
    const [timer, setTimer] = useState(null);
    const [tooltip, setTooltip] = useState(!disabled ? tip : '');
    const trackEvent = useTrackEvent(trackEventProps);
    const setDelay = (callback: VoidFunction, delay = 500) => setTimer(setTimeout(callback, delay));

    useEffect(() => {
        return () => clearTimeout(timer);
    }, []);

    const triggerCopy = (): boolean => {
        const el = root.document.createElement('textarea');
        el.value = copyText;
        el.setAttribute('readonly', '');
        el.style.position = 'absolute';
        el.style.width = '2em';
        el.style.left = '-100em';
        root.document.body.appendChild(el);
        el.select();
        try {
            root.document.execCommand('copy');
            trackEvent('Success', {data: {copyText}});
            return true;
        } catch (e) {
            // Can't copy, maybe older browser
            trackEvent('Fail', {data: {copyText, error: e}});
        } finally {
            root.document.body.removeChild(el);
        }
        return false;
    };

    const onClick = e => {
        e.stopPropagation();
        if (disabled) return;

        const isCopied = triggerCopy();
        if (onCopy) onCopy(isCopied);
        setTooltip(isCopied ? successTip : errorTip);

        setDelay(() => {
            setTooltip(tip);
        });
    };

    return (
        <Tooltip
            arrow
            className={className}
            placement="top"
            title={tooltip}
            onClick={onClick}
            PopperProps={{onClick}}
            disableFocusListener={disabled}
            disableHoverListener={disabled}
            disableTouchListener={disabled}>
            <span style={{display: 'inline-block'}}>{children}</span>
        </Tooltip>
    );
};

export default Copy;
